import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface LightboxImage {
  url: string;
  caption?: string;
}

export interface LightboxState {
  images: LightboxImage[];
  index: number;
}

// A single global lightbox (rendered once in app.component.html, same
// pattern as app-chatbot) that any component can open via this service —
// avoids wiring a separate modal per page everywhere a snapshot thumbnail
// shows up (entry form, admin history, the AlgoDesk Journal tab, ...).
// Opens a whole gallery (not just one image) so next()/prev() can step
// through the rest of that entry's attachments without closing and
// reopening.
@Injectable({ providedIn: 'root' })
export class ImageLightboxService {
  private readonly _state$ = new BehaviorSubject<LightboxState | null>(null);
  readonly state$ = this._state$.asObservable();

  open(images: LightboxImage[], startIndex = 0): void {
    if (!images.length) return;
    const index = Math.min(Math.max(startIndex, 0), images.length - 1);
    this._state$.next({ images, index });
  }

  close(): void {
    this._state$.next(null);
  }

  next(): void {
    const s = this._state$.value;
    if (!s || s.images.length < 2) return;
    this._state$.next({ ...s, index: (s.index + 1) % s.images.length });
  }

  prev(): void {
    const s = this._state$.value;
    if (!s || s.images.length < 2) return;
    this._state$.next({ ...s, index: (s.index - 1 + s.images.length) % s.images.length });
  }
}
